import React from 'react';
import { 
  Clock, 
  AlertTriangle, 
  XCircle, 
  CheckCircle,
  ArrowUp,
  Flame,
  FileSearch,
  Banknote
} from 'lucide-react';

/**
 * SLA Badge Component
 * 
 * Displays visual SLA status indicators based on user story requirements:
 * - "SLA tracking with visual dashboards" 
 * - Visual indicators for deadline compliance
 * - Color-coded status badges for quick identification
 */
const SlaBadge = ({ 
  slaStatus, 
  timeRemaining, 
  hoursOverdue, 
  escalationLevel, // eslint-disable-line no-unused-vars
  size = 'md',
  showText = true,
  showTooltip = true 
}) => {
  
  const getSlaConfig = () => {
    switch (slaStatus) {
      case 'WITHIN_SLA':
        return {
          icon: CheckCircle,
          color: 'bg-green-100 text-green-800 border-green-200',
          label: 'Within SLA',
          description: 'Claim is progressing within service level agreement',
          urgency: 'low'
        };
        
      case 'APPROACHING_BREACH':
        return {
          icon: AlertTriangle,
          color: 'bg-orange-100 text-orange-800 border-orange-200',
          label: 'Approaching Deadline',
          description: `${timeRemaining ? `${timeRemaining}h remaining` : 'SLA deadline approaching (80% elapsed)'}`,
          urgency: 'medium',
          animate: true
        };
        
      case 'BREACHED':
        return {
          icon: XCircle,
          color: 'bg-red-100 text-red-800 border-red-200',
          label: 'SLA Breached',
          description: `Overdue by ${hoursOverdue || 0} hours`,
          urgency: 'high',
          animate: true
        };
        
      case 'ESCALATED_L1':
        return {
          icon: ArrowUp,
          color: 'bg-purple-100 text-purple-800 border-purple-200',
          label: 'Escalated Level 1',
          description: 'Escalated to Claims Officers & Approvers',
          urgency: 'high',
          animate: true
        };
        
      case 'ESCALATED_L2':
        return {
          icon: ArrowUp,
          color: 'bg-purple-100 text-purple-800 border-purple-200',
          label: 'Escalated Level 2',
          description: 'Escalated to Finance & Admin Teams',
          urgency: 'critical',
          animate: true
        };
        
      case 'ESCALATED_L3':
        return {
          icon: Flame,
          color: 'bg-red-100 text-red-800 border-red-200',
          label: 'Escalated Level 3',
          description: 'CRITICAL: Senior Management Intervention Required',
          urgency: 'critical',
          animate: true
        };
        
      default:
        return {
          icon: Clock,
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          label: 'No SLA Data',
          description: 'SLA tracking not available',
          urgency: 'none'
        };
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return {
          container: 'px-2 py-1 text-xs',
          icon: 'w-3 h-3',
          text: 'text-xs'
        };
      case 'lg':
        return {
          container: 'px-4 py-2 text-sm',
          icon: 'w-5 h-5',
          text: 'text-sm'
        };
      default: // md
        return {
          container: 'px-3 py-1 text-sm',
          icon: 'w-4 h-4',
          text: 'text-sm'
        };
    }
  };

  const config = getSlaConfig();
  const sizeClasses = getSizeClasses();
  const Icon = config.icon;

  const badgeElement = (
    <div 
      className={`
        inline-flex items-center rounded-full border font-medium
        ${config.color} 
        ${sizeClasses.container}
        ${config.animate ? 'animate-pulse' : ''}
        ${config.urgency === 'critical' ? 'shadow-lg' : ''}
        ${showTooltip ? 'cursor-help' : ''}
      `}
      title={showTooltip ? config.description : undefined}
    >
      <Icon className={`${sizeClasses.icon} ${showText ? 'mr-1' : ''}`} />
      {showText && (
        <span className={sizeClasses.text}>
          {config.label}
        </span>
      )}
    </div>
  );

  // If tooltip is enabled and we want a more detailed tooltip
  if (showTooltip && config.urgency !== 'none') {
    return (
      <div className="relative group">
        {badgeElement}
        
        {/* Enhanced Tooltip */}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-50">
          <div className={`
            px-3 py-2 text-sm text-white rounded-lg shadow-lg max-w-xs
            ${config.urgency === 'critical' ? 'bg-red-600' : 
              config.urgency === 'high' ? 'bg-orange-600' :
              config.urgency === 'medium' ? 'bg-yellow-600' : 'bg-gray-600'}
          `}>
            <div className="font-semibold">{config.label}</div>
            <div className="text-xs mt-1">{config.description}</div>
            
            {/* SLA Requirements Info */}
            <div className="text-xs mt-2 opacity-90 border-t border-white/20 pt-2">
              <div className="font-medium">SLA Standards:</div>
              <div>• Death: 72h (3 days)</div>
              <div>• Disability: 120h (5 days)</div>
              <div>• Critical Illness: 96h (4 days)</div>
            </div>
            
            {/* Arrow */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-current"></div>
          </div>
        </div>
      </div>
    );
  }

  return badgeElement;
};

/**
 * SLA Status Indicator for Lists/Tables
 * Compact version for use in claims lists
 */
export const SlaStatusIndicator = ({ slaData, compact = false }) => {
  if (!slaData) {
    return (
      <SlaBadge 
        slaStatus="UNKNOWN" 
        size={compact ? 'sm' : 'md'}
        showText={!compact}
      />
    );
  }

  return (
    <SlaBadge 
      slaStatus={slaData.slaStatus}
      timeRemaining={slaData.timeRemaining}
      hoursOverdue={slaData.hoursOverdue}
      escalationLevel={slaData.escalationLevel}
      size={compact ? 'sm' : 'md'}
      showText={!compact}
    />
  );
};

/**
 * SLA Summary Card for Dashboard
 * Shows comprehensive SLA information with metrics
 */
export const SlaStatusCard = ({ slaData, claimType, claimStatus }) => {
  if (!slaData) return null;

  const getSlaHours = (type) => {
    switch (type) {
      case 'DEATH': return 72;
      case 'DISABILITY': return 120;
      case 'CRITICAL_ILLNESS': return 96;
      default: return 72;
    }
  };

  // Determine current phase
  const isPaymentPhase = claimStatus && ['APPROVED', 'PAYMENT_PENDING'].includes(claimStatus);
  const phaseLabel = isPaymentPhase ? 'Payment Processing Phase' : 'Claims Review Phase';
  const PhaseIcon = isPaymentPhase ? Banknote : FileSearch;
  const phaseColor = isPaymentPhase ? 'fresh-leaf' : 'info';

  // Use targetHours from backend if available (handles payment phase correctly)
  // Otherwise fallback to claim type calculation
  const slaHours = slaData.targetHours || getSlaHours(claimType);
  const progressPercentage = slaData.timeRemaining 
    ? Math.max(0, ((slaHours - slaData.timeRemaining) / slaHours) * 100)
    : 100;

  const getProgressColor = () => {
    if (progressPercentage >= 100) return 'bg-error';
    if (progressPercentage >= 80) return 'bg-warning';
    if (progressPercentage >= 60) return 'bg-warning/70';
    // Use phase-specific color for safe progress
    return isPaymentPhase ? 'bg-fresh-leaf' : 'bg-info';
  };

  return (
    <div className="bg-crystal-white shadow-soft border border-steel/20 rounded-lg overflow-hidden transition-all duration-300 ease-in-out hover:shadow-lg hover:border-fresh-leaf/30 p-6 space-y-4">
      {/* Phase Indicator Badge */}
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md border-l-4 ${
        isPaymentPhase 
          ? 'bg-fresh-leaf/10 border-fresh-leaf' 
          : 'bg-info/10 border-info'
      }`}>
        <PhaseIcon className={`w-4 h-4 ${isPaymentPhase ? 'text-fresh-leaf' : 'text-info'}`} />
        <span className={`text-xs font-medium uppercase tracking-wide ${
          isPaymentPhase ? 'text-fresh-leaf' : 'text-info'
        }`}>
          {phaseLabel}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-lg font-semibold text-graphite">
            {isPaymentPhase ? 'Payment SLA' : 'Claims Review SLA'}
          </h4>
          <p className="text-xs text-steel mt-0.5">
            {isPaymentPhase ? 'Payment processing deadline' : 'Processing & evaluation deadline'}
          </p>
        </div>
        <SlaBadge 
          slaStatus={slaData.slaStatus}
          timeRemaining={slaData.timeRemaining}
          hoursOverdue={slaData.hoursOverdue}
          escalationLevel={slaData.escalationLevel}
          size="sm"
          showText={false}
        />
      </div>

      {/* Progress Bar */}
      <div className="space-y-3">
        <div className="flex justify-between text-sm text-steel font-medium">
          <span>Progress</span>
          <span>{Math.round(progressPercentage)}%</span>
        </div>
        <div className="w-full bg-silver-mist rounded-full h-3 shadow-inner">
          <div 
            className={`h-3 rounded-full transition-all duration-500 ease-out ${getProgressColor()}`}
            style={{ width: `${Math.min(100, progressPercentage)}%` }}
          ></div>
        </div>
      </div>

      {/* Time Information */}
      <div className="grid grid-cols-2 gap-6 text-sm">
        <div className="space-y-1">
          <span className="text-steel font-medium">SLA Limit:</span>
          <div className="text-graphite font-semibold">{slaHours}h ({Math.floor(slaHours/24)}d)</div>
        </div>
        <div className="space-y-1">
          <span className="text-steel font-medium">
            {slaData.timeRemaining > 0 ? 'Time Left:' : 'Overdue:'}
          </span>
          <div className={`font-semibold ${slaData.timeRemaining > 0 ? 'text-fresh-leaf' : 'text-error'}`}>
            {slaData.timeRemaining > 0 
              ? `${slaData.timeRemaining}h`
              : `${slaData.hoursOverdue || 0}h`
            }
          </div>
        </div>
      </div>

      {/* Escalation Info */}
      {slaData.escalationLevel && (
        <div className="pt-4 border-t border-steel/15">
          <div className="text-sm text-steel font-medium mb-1">Escalation Level:</div>
          <div className="text-base font-semibold text-deep-jungle">
            {slaData.escalationLevel.replace('_', ' ')}
          </div>
        </div>
      )}
    </div>
  );
};

export default SlaBadge;
